// src/pages/admin/AdminRecovery.tsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from "react";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  FileText,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Calendar,
  Wallet,
  Hash,
  DollarSign,
  Check,
  MessageSquare,
  Mail,
  Phone,
  User,
  Shield,
  Filter,
  BarChart3,
  DownloadCloud,
  Bell,
  Copy,
} from "lucide-react";
import { recoveryService, transactionService } from "../../services/api";
import toast from "react-hot-toast";
import {
  formatAbbreviatedCurrency,
  formatCrypto,
  formatCurrency,
} from "../../utils/currency";
import RecoveryChat from "../../components/recovery/RecoveryChat";
import { useDispatch } from "react-redux";
import { getUnreadChatCount } from "../../redux/slices/recoveryChatSlice";

interface CryptoPrice {
  [key: string]: {
    price: number;
    network: string;
    crypto_id: string;
  };
}

// Initial currencies (same as Deposit.tsx)
const INITIAL_CURRENCIES = {
  BTC: { price: 0, network: "Bitcoin", crypto_id: "bitcoin" },
  ETH: { price: 0, network: "Ethereum", crypto_id: "ethereum" },
  USDT: { price: 0, network: "Ethereum", crypto_id: "tether" },
  BNB: { price: 0, network: "Binance Smart Chain", crypto_id: "binancecoin" },
  SOL: { price: 0, network: "Solana", crypto_id: "solana" },
};

interface RecoveryCase {
  id: number;
  case_number: string;
  user_name: string;
  user_email: string;
  wallet_address: string;
  transaction_id: string;
  amount: number;
  currency: string;
  network: string;
  status: string;
  recovery_fee: number | null;
  success_fee_percentage: number;
  estimated_recovery_amount: number | null;
  recovery_plan: string | null;
  additional_notes: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  assessment_completed_at?: string;
  recovery_started_at?: string;
  recovery_completed_at?: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    verification_level: number;
    wallet_connected: boolean;
  };
  // Add unread message count for this case
  unread_message_count?: number;
}

interface Statistics {
  overview: {
    total_requests: number;
    pending_requests: number;
    in_progress_requests: number;
    completed_requests: number;
    failed_requests: number;
    success_rate: number;
    total_amount_lost: number;
    estimated_recovery_amount: number;
    recovery_rate: number;
  };
  network_statistics: Array<{
    network: string;
    total: number;
    total_amount: number;
  }>;
  status_distribution: Array<{
    status: string;
    count: number;
  }>;
  monthly_trend: Array<{
    month: string;
    total_requests: number;
    total_amount: number;
  }>;
}

// View Details Modal Component
const RecoveryDetailsModal: React.FC<{
  caseItem: RecoveryCase | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusUpdate: (id: number, status: string, data?: any) => Promise<void>;
  onUpdateNotes: (id: number, notes: string) => Promise<void>;
  cryptoPrices: CryptoPrice;
}> = ({
  caseItem,
  isOpen,
  onClose,
  onStatusUpdate,
  onUpdateNotes,
  cryptoPrices,
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"details" | "notes">("details");
  const [newNotes, setNewNotes] = useState("");
  const [updatingNotes, setUpdatingNotes] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState({
    status: "",
    recovery_fee: "",
    success_fee_percentage: "",
    estimated_recovery_amount: "",
    recovery_plan: "",
    admin_notes: "",
  });

  if (!isOpen || !caseItem) return null;

  // Helper function to calculate USD value using cached prices
  const calculateUsdValue = (amount: number, currency: string) => {
    // Get the cached price from cryptoPrices
    const currentPrice = cryptoPrices[currency]?.price || 0;

    if (currentPrice > 0) {
      // Use cached price from localStorage
      return amount * currentPrice;
    } else {
      // This should rarely happen since we load from localStorage on startup
      // But as fallback, use realistic defaults (better than hardcoded 45000)
      const defaultRates: Record<string, number> = {
        BTC: 45000,
        ETH: 3000,
        USDT: 1,
        BNB: 400,
        SOL: 100,
      };
      const fallbackRate = defaultRates[currency] || 1;
      return amount * fallbackRate;
    }
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(`Copied ${field} to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const getExplorerUrl = (network: string, transactionId: string) => {
    const explorers: Record<string, string> = {
      Bitcoin: `https://blockchain.info/tx/${transactionId}`,
      Ethereum: `https://etherscan.io/tx/${transactionId}`,
      "Binance Smart Chain": `https://bscscan.com/tx/${transactionId}`,
      BSC: `https://bscscan.com/tx/${transactionId}`,
      Solana: `https://solscan.io/tx/${transactionId}`,
      Polygon: `https://polygonscan.com/tx/${transactionId}`,
      Arbitrum: `https://arbiscan.io/tx/${transactionId}`,
      "BNB Smart Chain": `https://bscscan.com/tx/${transactionId}`,
      TRON: `https://tronscan.org/#/transaction/${transactionId}`,
      Cardano: `https://cardanoscan.io/transaction/${transactionId}`,
      Litecoin: `https://blockchair.com/litecoin/transaction/${transactionId}`,
      Dogecoin: `https://blockchair.com/dogecoin/transaction/${transactionId}`,
    };
    return (
      explorers[network] || `https://blockchair.com/search?q=${transactionId}`
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  const handleUpdateNotes = async () => {
    if (!newNotes.trim()) return;

    setUpdatingNotes(true);
    try {
      await onUpdateNotes(caseItem.id, newNotes);
      setNewNotes("");
      toast.success("Notes updated successfully");
    } catch (error) {
      toast.error("Failed to update notes");
    } finally {
      setUpdatingNotes(false);
    }
  };

  const handleStatusSubmit = async () => {
    try {
      await onStatusUpdate(caseItem.id, statusUpdateData.status, {
        recovery_fee: statusUpdateData.recovery_fee
          ? parseFloat(statusUpdateData.recovery_fee)
          : null,
        success_fee_percentage: statusUpdateData.success_fee_percentage
          ? parseFloat(statusUpdateData.success_fee_percentage)
          : null,
        estimated_recovery_amount: statusUpdateData.estimated_recovery_amount
          ? parseFloat(statusUpdateData.estimated_recovery_amount)
          : null,
        recovery_plan: statusUpdateData.recovery_plan || null,
        admin_notes: statusUpdateData.admin_notes || null,
      });
      setShowStatusUpdate(false);
      setStatusUpdateData({
        status: "",
        recovery_fee: "",
        success_fee_percentage: "",
        estimated_recovery_amount: "",
        recovery_plan: "",
        admin_notes: "",
      });
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-100 dark:bg-green-900/20";
      case "in_progress":
      case "under_review":
      case "assessment_complete":
      case "recovery_plan_sent":
      case "recovery_approved":
        return "text-blue-600 bg-blue-100 dark:bg-blue-900/20";
      case "failed":
        return "text-red-600 bg-red-100 dark:bg-red-900/20";
      case "cancelled":
        return "text-gray-600 bg-gray-100 dark:bg-gray-900/20";
      default:
        return "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20";
    }
  };

  const formatStatus = (status: string): string => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${getStatusColor(caseItem.status)}`}
              >
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Recovery Case: {caseItem.case_number}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                      caseItem.status
                    )}`}
                  >
                    {formatStatus(caseItem.status)}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    • Submitted {formatDate(caseItem.created_at)}
                  </span>
                  {caseItem.unread_message_count &&
                    caseItem.unread_message_count > 0 && (
                      <span className="flex items-center gap-1 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                        <Bell className="h-3 w-3" />
                        {caseItem.unread_message_count} new message
                        {caseItem.unread_message_count !== 1 ? "s" : ""}
                      </span>
                    )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowStatusUpdate(true)}
                className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Update Status
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="px-6 flex space-x-4">
              <button
                onClick={() => setActiveTab("details")}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "details"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                Case Details
              </button>
              <button
                onClick={() => setActiveTab("notes")}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "notes"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                Admin Notes
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div
            className="overflow-y-auto p-6"
            style={{ maxHeight: "calc(90vh - 200px)" }}
          >
            {activeTab === "details" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Case Info */}
                  <div className={GlobalStyles.card}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Case Information
                    </h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Case Number
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.case_number}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Status
                          </p>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                              caseItem.status
                            )}`}
                          >
                            {formatStatus(caseItem.status)}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Network
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.network}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Currency
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.currency}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Submission Date
                        </p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formatDate(caseItem.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* User Info */}
                  <div className={GlobalStyles.card}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      User Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          User Name
                        </p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {caseItem.user_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Email
                        </p>
                        <div className="flex items-center justify-between group">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.user_email}
                          </p>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() =>
                                handleCopy(caseItem.user_email, "Email")
                              }
                              title="Copy email"
                            >
                              {copiedField === "Email" ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                            <a
                              href={`mailto:${caseItem.user_email}`}
                              className="text-blue-500 hover:text-blue-600"
                              title="Send email"
                            >
                              <Mail className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                      {caseItem.user?.phone && (
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Phone
                          </p>
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {caseItem.user.phone}
                            </p>
                            <a
                              href={`tel:${caseItem.user.phone}`}
                              className="text-blue-500 hover:text-blue-600"
                              title="Call"
                            >
                              <Phone className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Verification Level
                          </p>
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              caseItem.user?.verification_level === 3
                                ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
                                : caseItem.user?.verification_level === 2
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300"
                                : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"
                            }`}
                          >
                            Level {caseItem.user?.verification_level}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Wallet Connected
                          </p>
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              caseItem.user?.wallet_connected
                                ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
                                : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"
                            }`}
                          >
                            {caseItem.user?.wallet_connected ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Wallet Info */}
                  <div className={GlobalStyles.card}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <Wallet className="h-4 w-4 mr-2" />
                      Wallet Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Wallet Address
                        </p>
                        <div className="flex items-center justify-between group">
                          <p className="font-mono text-sm truncate max-w-[80%] text-gray-900 dark:text-white">
                            {caseItem.wallet_address}
                          </p>
                          <button
                            onClick={() =>
                              handleCopy(
                                caseItem.wallet_address,
                                "Wallet Address"
                              )
                            }
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Copy address"
                          >
                            {copiedField === "Wallet Address" ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Transaction Info */}
                  <div className={GlobalStyles.card}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <Hash className="h-4 w-4 mr-2" />
                      Transaction Details
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Transaction ID
                        </p>
                        <div className="flex items-center justify-between group">
                          <p className="font-mono text-sm truncate max-w-[70%] text-gray-900 dark:text-white">
                            {caseItem.transaction_id}
                          </p>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() =>
                                handleCopy(
                                  caseItem.transaction_id,
                                  "Transaction ID"
                                )
                              }
                              title="Copy transaction ID"
                            >
                              {copiedField === "Transaction ID" ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                            <a
                              href={getExplorerUrl(
                                caseItem.network,
                                caseItem.transaction_id
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-600"
                              title="View on explorer"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Amount Lost
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {formatCrypto(caseItem.amount, caseItem.currency)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            USD Value
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            ~$
                            {formatCurrency(
                              calculateUsdValue(
                                caseItem.amount,
                                caseItem.currency
                              )
                            )}
                          </p>
                        </div>
                      </div>
                      {caseItem.additional_notes && (
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            User Notes
                          </p>
                          <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {caseItem.additional_notes}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Recovery Info */}
                  <div className={GlobalStyles.card}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Recovery Information
                    </h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Estimated Recovery
                          </p>
                          <p className="font-medium text-green-600 dark:text-green-400">
                            {caseItem.estimated_recovery_amount
                              ? formatCrypto(
                                  caseItem.estimated_recovery_amount,
                                  caseItem.currency
                                )
                              : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Success Fee
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.success_fee_percentage > 0
                              ? `${caseItem.success_fee_percentage}%`
                              : "—"}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Recovery Fee
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.recovery_fee
                              ? formatCrypto(
                                  caseItem.recovery_fee,
                                  caseItem.currency
                                )
                              : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Net Recovery
                          </p>
                          <p className="font-medium text-green-600 dark:text-green-400">
                            {caseItem.estimated_recovery_amount &&
                            caseItem.recovery_fee
                              ? formatCrypto(
                                  caseItem.estimated_recovery_amount -
                                    caseItem.recovery_fee,
                                  caseItem.currency
                                )
                              : "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recovery Timeline */}
                  <div className={GlobalStyles.card}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      Recovery Timeline
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400">
                          Submitted:
                        </span>
                        <span className="font-medium">
                          {formatDate(caseItem.created_at)}
                        </span>
                      </div>
                      {caseItem.assessment_completed_at && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            Assessment Completed:
                          </span>
                          <span className="font-medium">
                            {formatDate(caseItem.assessment_completed_at)}
                          </span>
                        </div>
                      )}
                      {caseItem.recovery_started_at && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            Recovery Started:
                          </span>
                          <span className="font-medium">
                            {formatDate(caseItem.recovery_started_at)}
                          </span>
                        </div>
                      )}
                      {caseItem.recovery_completed_at && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            Recovery Completed:
                          </span>
                          <span className="font-medium">
                            {formatDate(caseItem.recovery_completed_at)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400">
                          Last Updated:
                        </span>
                        <span className="font-medium">
                          {formatDate(caseItem.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "notes" && (
              <div className="space-y-6">
                <div className={GlobalStyles.card}>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                    Current Admin Notes
                  </h4>
                  {caseItem.admin_notes ? (
                    <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {caseItem.admin_notes}
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 italic">
                      No admin notes yet.
                    </p>
                  )}
                </div>

                <div className={GlobalStyles.card}>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                    Add/Update Notes
                  </h4>
                  <textarea
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    placeholder="Add internal notes about this case..."
                    className={`${GlobalStyles.input} min-h-[150px] resize-none`}
                  />
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={handleUpdateNotes}
                      disabled={!newNotes.trim() || updatingNotes}
                      className={`${GlobalStyles.button} ${
                        GlobalStyles.buttonPrimary
                      } px-4 py-2 ${
                        !newNotes.trim() || updatingNotes
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {updatingNotes ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Update Notes"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Status Update Modal */}
          {showStatusUpdate && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Update Case Status
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className={GlobalStyles.inputLabel}>Status</label>
                      <select
                        value={statusUpdateData.status}
                        onChange={(e) =>
                          setStatusUpdateData({
                            ...statusUpdateData,
                            status: e.target.value,
                          })
                        }
                        className={GlobalStyles.input}
                      >
                        <option value="">Select Status</option>
                        <option value="under_review">Under Review</option>
                        <option value="assessment_complete">
                          Assessment Complete
                        </option>
                        <option value="recovery_plan_sent">
                          Recovery Plan Sent
                        </option>
                        <option value="recovery_approved">
                          Recovery Approved
                        </option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    {statusUpdateData.status === "assessment_complete" && (
                      <>
                        <div>
                          <label className={GlobalStyles.inputLabel}>
                            Estimated Recovery Amount
                          </label>
                          <input
                            type="number"
                            step="0.00000001"
                            value={statusUpdateData.estimated_recovery_amount}
                            onChange={(e) =>
                              setStatusUpdateData({
                                ...statusUpdateData,
                                estimated_recovery_amount: e.target.value,
                              })
                            }
                            className={GlobalStyles.input}
                            placeholder={`Amount in ${caseItem.currency}`}
                          />
                        </div>
                        <div>
                          <label className={GlobalStyles.inputLabel}>
                            Success Fee Percentage
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            max="100"
                            value={statusUpdateData.success_fee_percentage}
                            onChange={(e) =>
                              setStatusUpdateData({
                                ...statusUpdateData,
                                success_fee_percentage: e.target.value,
                              })
                            }
                            className={GlobalStyles.input}
                            placeholder="20"
                          />
                        </div>
                      </>
                    )}

                    {(statusUpdateData.status === "in_progress" ||
                      statusUpdateData.status === "completed") && (
                      <div>
                        <label className={GlobalStyles.inputLabel}>
                          Recovery Fee
                        </label>
                        <input
                          type="number"
                          step="0.00000001"
                          value={statusUpdateData.recovery_fee}
                          onChange={(e) =>
                            setStatusUpdateData({
                              ...statusUpdateData,
                              recovery_fee: e.target.value,
                            })
                          }
                          className={GlobalStyles.input}
                          placeholder={`Fee amount in ${caseItem.currency}`}
                        />
                      </div>
                    )}

                    <div>
                      <label className={GlobalStyles.inputLabel}>
                        Recovery Plan (Optional)
                      </label>
                      <textarea
                        value={statusUpdateData.recovery_plan}
                        onChange={(e) =>
                          setStatusUpdateData({
                            ...statusUpdateData,
                            recovery_plan: e.target.value,
                          })
                        }
                        className={`${GlobalStyles.input} min-h-[100px] resize-none`}
                        placeholder="Enter recovery plan details..."
                      />
                    </div>

                    <div>
                      <label className={GlobalStyles.inputLabel}>
                        Admin Notes (Optional)
                      </label>
                      <textarea
                        value={statusUpdateData.admin_notes}
                        onChange={(e) =>
                          setStatusUpdateData({
                            ...statusUpdateData,
                            admin_notes: e.target.value,
                          })
                        }
                        className={`${GlobalStyles.input} min-h-[80px] resize-none`}
                        placeholder="Add internal notes..."
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setShowStatusUpdate(false)}
                      className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonMedium}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleStatusSubmit}
                      disabled={!statusUpdateData.status}
                      className={`${GlobalStyles.button} ${
                        GlobalStyles.buttonPrimary
                      } ${GlobalStyles.buttonMedium} ${
                        !statusUpdateData.status
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      Update Status
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Statistics Modal
const StatisticsModal: React.FC<{
  statistics: Statistics | null;
  isOpen: boolean;
  onClose: () => void;
  cryptoPrices: CryptoPrice;
}> = ({ statistics, isOpen, onClose, cryptoPrices }) => {
  if (!isOpen || !statistics) return null;

  // Helper function to calculate USD value using cached prices
  const calculateUsdValue = (amount: number, currency: string) => {
    // Get the cached price from cryptoPrices
    const currentPrice = cryptoPrices[currency]?.price || 0;

    if (currentPrice > 0) {
      // Use cached price from localStorage
      return amount * currentPrice;
    } else {
      // This should rarely happen since we load from localStorage on startup
      // But as fallback, use realistic defaults
      const defaultRates: Record<string, number> = {
        BTC: 45000,
        ETH: 3000,
        USDT: 1,
        BNB: 400,
        SOL: 100,
      };
      const fallbackRate = defaultRates[currency] || 1;
      return amount * fallbackRate;
    }
  };

  const formatStatus = (status: string): string => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-6 w-6 text-primary" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Recovery Statistics
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div
            className="overflow-y-auto p-6"
            style={{ maxHeight: "calc(90vh - 120px)" }}
          >
            {/* Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className={GlobalStyles.card}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Cases
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {statistics.overview.total_requests}
                </p>
              </div>
              <div className={GlobalStyles.card}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Success Rate
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {statistics.overview.success_rate}%
                </p>
              </div>
              <div className={GlobalStyles.card}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Amount Lost
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  $
                  {formatCurrency(
                    calculateUsdValue(
                      statistics.overview.total_amount_lost,
                      "BTC"
                    )
                  )}
                </p>
              </div>
              <div className={GlobalStyles.card}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Recovery Rate
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {statistics.overview.recovery_rate}%
                </p>
              </div>
            </div>

            {/* Network Statistics */}
            <div className={GlobalStyles.card + " mb-6"}>
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                Network Statistics
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-3 font-medium">Network</th>
                      <th className="pb-3 font-medium">Cases</th>
                      <th className="pb-3 font-medium">Total Amount</th>
                      <th className="pb-3 font-medium">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {statistics.network_statistics.map((stat, index) => (
                      <tr key={index}>
                        <td className="py-3">
                          <span className={`${GlobalStyles.badge}`}>
                            {stat.network}
                          </span>
                        </td>
                        <td className="py-3 font-medium">{stat.total}</td>
                        <td className="py-3">
                          $
                          {formatCurrency(
                            calculateUsdValue(stat.total_amount, "BTC")
                          )}
                        </td>
                        <td className="py-3">
                          {(
                            (stat.total_amount /
                              statistics.overview.total_amount_lost) *
                            100
                          ).toFixed(1)}
                          %
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Status Distribution */}
            <div className={GlobalStyles.card + " mb-6"}>
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                Status Distribution
              </h4>
              <div className="space-y-3">
                {statistics.status_distribution.map((stat, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {formatStatus(stat.status)}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{stat.count}</span>
                      <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{
                            width: `${
                              (stat.count /
                                statistics.overview.total_requests) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                      <span className="text-sm text-gray-500 w-10">
                        {(
                          (stat.count / statistics.overview.total_requests) *
                          100
                        ).toFixed(1)}
                        %
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly Trend */}
            <div className={GlobalStyles.card}>
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                Monthly Trend
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-3 font-medium">Month</th>
                      <th className="pb-3 font-medium">Cases</th>
                      <th className="pb-3 font-medium">Total Amount</th>
                      <th className="pb-3 font-medium">Avg. per Case</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {statistics.monthly_trend.map((trend, index) => (
                      <tr key={index}>
                        <td className="py-3 text-sm">{trend.month}</td>
                        <td className="py-3 font-medium">
                          {trend.total_requests}
                        </td>
                        <td className="py-3">
                          $
                          {formatCurrency(
                            calculateUsdValue(trend.total_amount, "BTC")
                          )}
                        </td>
                        <td className="py-3">
                          $
                          {formatCurrency(
                            calculateUsdValue(trend.total_amount, "BTC") /
                              trend.total_requests
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminRecovery: React.FC = () => {
  const dispatch = useDispatch();
  const [recoveryCases, setRecoveryCases] = useState<RecoveryCase[]>([]);
  const [filter, setFilter] = useState({
    status: "",
    network: "",
    search: "",
    date_from: "",
    date_to: "",
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });
  const [selectedCase, setSelectedCase] = useState<RecoveryCase | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [updatingCaseId, setUpdatingCaseId] = useState<number | null>(null);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedCases, setSelectedCases] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState("");
  // eslint-disable-next-line
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showChatModal, setShowChatModal] = useState<{
    isOpen: boolean;
    caseId: number;
    caseNumber: string;
  }>({
    isOpen: false,
    caseId: 0,
    caseNumber: "",
  });
  const [cryptoPrices, setCryptoPrices] = useState<CryptoPrice>(() => {
    // Try to load saved prices from localStorage on initial render (EXACTLY like Deposit.tsx)
    const savedPrices = localStorage.getItem("obelisk_crypto_prices");
    if (savedPrices) {
      try {
        const parsed = JSON.parse(savedPrices);
        // Merge with initial currencies to ensure all required fields exist
        return { ...INITIAL_CURRENCIES, ...parsed };
      } catch (e) {
        return INITIAL_CURRENCIES;
      }
    }
    return INITIAL_CURRENCIES;
  });
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>(() => {
    // Try to load last update time from localStorage
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    return savedUpdate || "";
  });

  // Refresh interval for checking new messages
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  useEffect(() => {
    fetchRecoveryCases();
    fetchStatistics();
    fetchCryptoPrices(); // Load cached prices on mount
    // Start polling for new messages
    startPolling();

    // Refresh prices every 60 seconds (same as Deposit.tsx)
    const priceInterval = setInterval(fetchCryptoPrices, 60000);

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
      clearInterval(priceInterval);
    };
    // eslint-disable-next-line
  }, [
    filter.status,
    filter.network,
    pagination.current_page,
    filter.date_from,
    filter.date_to,
  ]);

  // EXACTLY the same function as in Deposit.tsx
  const savePricesToStorage = (prices: CryptoPrice, timestamp: number) => {
    try {
      // Save prices to localStorage
      localStorage.setItem("obelisk_crypto_prices", JSON.stringify(prices));

      // Save last update timestamp
      localStorage.setItem(
        "obelisk_prices_last_update_timestamp",
        timestamp.toString()
      );

      // Save formatted last update time for display
      const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
      setLastUpdate(updateTime);
      localStorage.setItem("obelisk_prices_last_update", updateTime);
    } catch (error) {
      console.error("Error saving prices to storage:", error);
    }
  };

  // EXACTLY the same function as in Deposit.tsx (with minor adjustments)
  const fetchCryptoPrices = async () => {
    setFetchingPrices(true);
    try {
      const response = await transactionService.getCryptoPrices();

      if (response.success && response.prices) {
        // Merge new prices with existing ones (so we don't lose old values)
        const mergedPrices = { ...cryptoPrices };
        let hasNewData = false;

        Object.entries(response.prices).forEach(([currency, data]) => {
          // Type-safe check for price data
          const priceData = data as any;
          if (
            priceData &&
            typeof priceData === "object" &&
            typeof priceData.price === "number" &&
            priceData.price > 0
          ) {
            mergedPrices[currency] = priceData;
            hasNewData = true;
          }
        });

        // Only update and save if we got new valid data
        if (hasNewData) {
          setCryptoPrices(mergedPrices);
          savePricesToStorage(
            mergedPrices,
            response.timestamp || Math.floor(Date.now() / 1000)
          );

          // Show success toast only if we actually got new data
          if (Object.keys(response.prices).length > 0) {
          }
        } else {
          // No new valid data, keep existing prices
        }
      } else {
        // API call succeeded but returned no data or error
        toast.success("Using cached prices - API returned no data");

        // Load saved timestamp if available
        const savedTimestamp = localStorage.getItem(
          "obelisk_prices_last_update_timestamp"
        );
        if (savedTimestamp) {
          const timestamp = parseInt(savedTimestamp);
          const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
          setLastUpdate(updateTime);
        }
      }
    } catch (error: any) {
      console.error("Price fetch error:", error);

      // On network error, use cached prices (EXACTLY like Deposit.tsx)
      const savedTimestamp = localStorage.getItem(
        "obelisk_prices_last_update_timestamp"
      );
      if (savedTimestamp) {
        const timestamp = parseInt(savedTimestamp);
        const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
        setLastUpdate(updateTime);
        toast.error("Network error - Using cached prices");
      } else {
        toast.error("Failed to fetch prices - Please try again");
      }
    } finally {
      setFetchingPrices(false);
    }
  };

  // Helper function to calculate USD value using cached prices
  const calculateUsdValue = (amount: number, currency: string) => {
    // Get the cached price from localStorage (via cryptoPrices state)
    const currentPrice = cryptoPrices[currency]?.price || 0;

    if (currentPrice > 0) {
      // Use cached price from localStorage
      return amount * currentPrice;
    } else {
      // This should rarely happen since we load from localStorage on startup
      // But as fallback, use realistic defaults
      const defaultRates: Record<string, number> = {
        BTC: 45000,
        ETH: 3000,
        USDT: 1,
        BNB: 400,
        SOL: 100,
      };
      const fallbackRate = defaultRates[currency] || 1;
      return amount * fallbackRate;
    }
  };

  const startPolling = () => {
    // Clear existing interval
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }

    // Set up new interval to check for new messages every 30 seconds
    const interval = setInterval(() => {
      checkForNewMessages();
    }, 30000);

    setRefreshInterval(interval);
  };

  const checkForNewMessages = async () => {
    try {
      await dispatch(getUnreadChatCount() as any);
      // Refetch cases to update unread counts
      fetchRecoveryCases();
    } catch (error) {
      console.error("Error checking for new messages:", error);
    }
  };

  const fetchRecoveryCases = async () => {
    setLoading(true);
    try {
      const response = await recoveryService.getRecoveryRequests({
        status: filter.status || undefined,
        network: filter.network || undefined,
        search: filter.search || undefined,
        date_from: filter.date_from || undefined,
        date_to: filter.date_to || undefined,
        page: pagination.current_page,
        per_page: pagination.per_page,
      });

      let apiData;
      if (response.data) {
        apiData = response;
      } else if (Array.isArray(response)) {
        apiData = {
          data: response,
          current_page: 1,
          last_page: 1,
          per_page: response.length,
          total: response.length,
          from: 1,
          to: response.length,
        };
      } else {
        apiData = response;
      }

      const transformedCases = (apiData.data || []).map((item: any) => {
        let userName = "Unknown User";
        let userEmail = "N/A";

        if (item.user) {
          userName = `${item.user.first_name || ""} ${
            item.user.last_name || ""
          }`.trim();
          userEmail = item.user.email || "N/A";
        } else if (item.user_name) {
          userName = item.user_name;
        } else if (item.user_email) {
          userEmail = item.user_email;
        }

        // IMPORTANT: Make sure unread_message_count is included
        const unreadCount = item.unread_message_count || 0;

        return {
          id: item.id,
          case_number: item.case_number,
          user_name: userName || `User #${item.user_id}`,
          user_email: userEmail || `user${item.user_id}@example.com`,
          wallet_address: item.wallet_address || "",
          transaction_id: item.transaction_id || "",
          amount: parseFloat(item.amount) || 0,
          currency: item.currency || "BTC",
          network: item.network || "Unknown",
          status: item.status || "submitted",
          recovery_fee: item.recovery_fee
            ? parseFloat(item.recovery_fee)
            : null,
          success_fee_percentage: item.success_fee_percentage
            ? parseFloat(item.success_fee_percentage)
            : 0,
          estimated_recovery_amount: item.estimated_recovery_amount
            ? parseFloat(item.estimated_recovery_amount)
            : null,
          recovery_plan: item.recovery_plan || null,
          additional_notes: item.additional_notes || null,
          admin_notes: item.admin_notes || null,
          created_at: item.created_at || new Date().toISOString(),
          updated_at: item.updated_at || new Date().toISOString(),
          assessment_completed_at: item.assessment_completed_at,
          recovery_started_at: item.recovery_started_at,
          recovery_completed_at: item.recovery_completed_at,
          user: item.user,
          // Make sure this is included
          unread_message_count: unreadCount,
        };
      });

      setRecoveryCases(transformedCases);
      setPagination({
        current_page: apiData.current_page || 1,
        last_page: apiData.last_page || 1,
        per_page: apiData.per_page || 20,
        total: apiData.total || 0,
      });

      setSelectedCases([]);
    } catch (error: any) {
      console.error("Error fetching recovery cases:", error);
      toast.error(
        error.response?.data?.error || "Failed to load recovery cases"
      );
      setRecoveryCases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await recoveryService.getRecoveryStatistics();
      setStatistics(response);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  const handleStatusUpdate = async (id: number, status: string, data?: any) => {
    setUpdatingCaseId(id);

    try {
      await recoveryService.updateRecoveryStatus(id, { status, ...data });
      toast.success(`Recovery case status updated to ${formatStatus(status)}`);
      fetchRecoveryCases();
      if (selectedCase?.id === id) {
        setSelectedCase(null);
        setDetailsModalOpen(false);
      }
    } catch (error: any) {
      console.error("Status update error:", error);
      toast.error(
        error.response?.data?.error || "Failed to update recovery status"
      );
    } finally {
      setUpdatingCaseId(null);
    }
  };

  const handleUpdateNotes = async (id: number, notes: string) => {
    try {
      await recoveryService.addRecoveryNotes(id, notes);
      fetchRecoveryCases();
    } catch (error: any) {
      console.error("Update notes error:", error);
      throw error;
    }
  };

  const handleExportReport = async () => {
    setExporting(true);
    try {
      const startDate =
        filter.date_from ||
        new Date(new Date().setMonth(new Date().getMonth() - 1))
          .toISOString()
          .split("T")[0];
      const endDate = filter.date_to || new Date().toISOString().split("T")[0];

      const response = await recoveryService.downloadRecoveryReport({
        start_date: startDate,
        end_date: endDate,
        format: "csv",
      });

      if (response.data) {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `recovery_report_${startDate}_to_${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Handle JSON response
        const dataStr = JSON.stringify(response, null, 2);
        const dataBlob = new Blob([dataStr], { type: "application/json" });
        const url = window.URL.createObjectURL(dataBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `recovery_report_${startDate}_to_${endDate}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      toast.success("Report exported successfully");
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error(
        error.response?.data?.error ||
          error.message ||
          "Failed to export report"
      );
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.last_page) {
      setPagination((prev) => ({ ...prev, current_page: page }));
    }
  };

  const handleCaseSelect = (id: number) => {
    setSelectedCases((prev) =>
      prev.includes(id) ? prev.filter((caseId) => caseId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedCases.length === recoveryCases.length) {
      setSelectedCases([]);
    } else {
      setSelectedCases(recoveryCases.map((caseItem) => caseItem.id));
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedCases.length === 0) return;

    try {
      if (bulkAction === "delete") {
        // Implement bulk delete
        toast.error("Bulk delete not implemented yet");
      } else if (
        ["under_review", "in_progress", "completed", "failed"].includes(
          bulkAction
        )
      ) {
        // Implement bulk status update
        for (const caseId of selectedCases) {
          await recoveryService.updateRecoveryStatus(caseId, {
            status: bulkAction,
          });
        }
        toast.success(
          `Updated ${selectedCases.length} cases to ${formatStatus(bulkAction)}`
        );
        fetchRecoveryCases();
        setSelectedCases([]);
        setBulkAction("");
        setShowBulkActions(false);
      }
    } catch (error) {
      toast.error("Failed to perform bulk action");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "in_progress":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "cancelled":
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return GlobalStyles.badgeSuccess;
      case "in_progress":
      case "under_review":
      case "assessment_complete":
      case "recovery_plan_sent":
      case "recovery_approved":
        return GlobalStyles.badgeInfo;
      case "failed":
        return GlobalStyles.badgeError;
      case "cancelled":
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
      default:
        return GlobalStyles.badgeWarning;
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const openDetailsModal = (caseItem: RecoveryCase) => {
    setSelectedCase(caseItem);
    setDetailsModalOpen(true);
  };

  const clearFilters = () => {
    setFilter({
      status: "",
      network: "",
      search: "",
      date_from: "",
      date_to: "",
    });
  };

  const handleChatOpen = (caseItem: RecoveryCase) => {
    setShowChatModal({
      isOpen: true,
      caseId: caseItem.id,
      caseNumber: caseItem.case_number,
    });
  };

  const handleRefresh = async () => {
    await fetchRecoveryCases();
    await fetchCryptoPrices();
    await dispatch(getUnreadChatCount() as any);
    toast.success("Refreshed recovery cases and prices");
  };

  // Get last update time for display (same as Deposit.tsx)
  const getLastUpdateDisplay = () => {
    if (fetchingPrices) return "Updating...";
    if (lastUpdate) return `Last updated: ${lastUpdate}`;

    // Try to get from localStorage if state is empty
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    if (savedUpdate) {
      setLastUpdate(savedUpdate);
      return `Last updated: ${savedUpdate}`;
    }

    return "Never updated";
  };

  // Calculate total unread messages across all cases
  const totalUnreadMessages = recoveryCases.reduce(
    (total, caseItem) => total + (caseItem.unread_message_count || 0),
    0
  );

  // Calculate cases with unread messages
  const casesWithUnreadMessages = recoveryCases.filter(
    (caseItem) => (caseItem.unread_message_count || 0) > 0
  ).length;

  if (loading && recoveryCases.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">
          Loading recovery cases...
        </span>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className={GlobalStyles.heading1}>Recovery Cases</h1>
              <p className={GlobalStyles.bodyText + " mt-2"}>
                Manage and monitor fund recovery cases
                {totalUnreadMessages > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    <Bell className="h-3 w-3" />
                    {totalUnreadMessages} unread message
                    {totalUnreadMessages !== 1 ? "s" : ""} across{" "}
                    {casesWithUnreadMessages} case
                    {casesWithUnreadMessages !== 1 ? "s" : ""}
                  </span>
                )}
                <span className="block text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {getLastUpdateDisplay()}
                </span>
              </p>
            </div>
            <div className="flex max-sm:flex-wrap items-center gap-3">
              <button
                onClick={() => setShowStatistics(true)}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Statistics
              </button>
              <button
                onClick={handleExportReport}
                disabled={exporting}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <DownloadCloud className="h-4 w-4 mr-2" />
                )}
                Export Report
              </button>
              <button
                onClick={fetchCryptoPrices}
                disabled={fetchingPrices}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
              >
                {fetchingPrices ? (
                  <div className="flex items-center">
                    <div
                      className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                    ></div>
                    Updating...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Prices
                  </div>
                )}
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh Cases
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div
          className={`${GlobalStyles.gridCols4} gap-6 ${GlobalStyles.sectionSpacing}`}
        >
          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Cases
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {pagination.total}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {
                    recoveryCases.filter((c) =>
                      [
                        "submitted",
                        "under_review",
                        "assessment_complete",
                        "recovery_plan_sent",
                        "recovery_approved",
                      ].includes(c.status)
                    ).length
                  }{" "}
                  pending
                </p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  In Progress
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {
                    recoveryCases.filter((c) => c.status === "in_progress")
                      .length
                  }
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Active recovery
                </p>
              </div>
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/20">
                <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Unread Messages
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {totalUnreadMessages}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {casesWithUnreadMessages} case
                  {casesWithUnreadMessages !== 1 ? "s" : ""} need attention
                </p>
              </div>
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/20">
                <Bell className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Value
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {formatAbbreviatedCurrency(
                    recoveryCases.reduce(
                      (sum, c) => sum + calculateUsdValue(c.amount, c.currency),
                      0
                    )
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Using current prices
                </p>
              </div>
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/20">
                <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`${GlobalStyles.card} ${GlobalStyles.sectionSpacing}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={GlobalStyles.heading3}>Filters</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center gap-1 text-sm text-primary"
              >
                <Filter className="h-4 w-4" />
                {showAdvancedFilters ? "Hide Advanced" : "Advanced Filters"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className={GlobalStyles.inputLabel}>Status</label>
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
                className={`${GlobalStyles.input} min-w-[150px]`}
              >
                <option value="">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="assessment_complete">Assessment Complete</option>
                <option value="recovery_plan_sent">Recovery Plan Sent</option>
                <option value="recovery_approved">Recovery Approved</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className={GlobalStyles.inputLabel}>Network</label>
              <select
                value={filter.network}
                onChange={(e) =>
                  setFilter({ ...filter, network: e.target.value })
                }
                className={`${GlobalStyles.input} min-w-[150px]`}
              >
                <option value="">All Networks</option>
                <option value="Bitcoin">Bitcoin</option>
                <option value="Ethereum">Ethereum</option>
                <option value="Binance Smart Chain">Binance Smart Chain</option>
                <option value="Solana">Solana</option>
                <option value="Polygon">Polygon</option>
                <option value="Arbitrum">Arbitrum</option>
                <option value="TRON">TRON</option>
                <option value="Cardano">Cardano</option>
              </select>
            </div>
            <div>
              <label className={GlobalStyles.inputLabel}>Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by case number, user, wallet, or transaction ID..."
                  value={filter.search}
                  onChange={(e) =>
                    setFilter({ ...filter, search: e.target.value })
                  }
                  className={`${GlobalStyles.input} pl-10`}
                />
              </div>
            </div>
          </div>

          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <label className={GlobalStyles.inputLabel}>Date From</label>
                <input
                  type="date"
                  value={filter.date_from}
                  onChange={(e) =>
                    setFilter({ ...filter, date_from: e.target.value })
                  }
                  className={GlobalStyles.input}
                />
              </div>
              <div>
                <label className={GlobalStyles.inputLabel}>Date To</label>
                <input
                  type="date"
                  value={filter.date_to}
                  onChange={(e) =>
                    setFilter({ ...filter, date_to: e.target.value })
                  }
                  className={GlobalStyles.input}
                />
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedCases.length > 0 && (
          <div
            className={`${GlobalStyles.card} ${GlobalStyles.sectionSpacing}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedCases.length} cases selected
                </span>
                <div className="relative">
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                    className={`${GlobalStyles.input} text-sm py-1.5 pr-8 pl-3 min-w-[140px]`}
                  >
                    <option value="">Bulk Actions</option>
                    <option value="under_review">Mark as Under Review</option>
                    <option value="in_progress">Mark as In Progress</option>
                    <option value="completed">Mark as Completed</option>
                    <option value="failed">Mark as Failed</option>
                  </select>
                </div>
                <button
                  onClick={handleBulkAction}
                  disabled={!bulkAction}
                  className={`${GlobalStyles.button} ${
                    GlobalStyles.buttonPrimary
                  } px-4 py-1.5 text-sm ${
                    !bulkAction ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Apply
                </button>
              </div>
              <button
                onClick={() => setSelectedCases([])}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Recovery Cases Table */}
        <div className={GlobalStyles.card}>
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead>
                <tr className={`${GlobalStyles.tableHeader} text-left`}>
                  <th className="px-4 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={
                        selectedCases.length === recoveryCases.length &&
                        recoveryCases.length > 0
                      }
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className={GlobalStyles.tableCell}>Case #</th>
                  <th className={`${GlobalStyles.tableCell} min-w-[120px]`}>
                    Chat
                  </th>
                  <th className={GlobalStyles.tableCell}>User</th>
                  <th className={GlobalStyles.tableCell}>Amount</th>
                  <th className={GlobalStyles.tableCell}>Network</th>
                  <th className={GlobalStyles.tableCell}>Status</th>
                  <th className={GlobalStyles.tableCell}>Created</th>
                  <th className={GlobalStyles.tableCell}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recoveryCases.length > 0 ? (
                  recoveryCases.map((caseItem) => (
                    <tr key={caseItem.id} className={GlobalStyles.tableRow}>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedCases.includes(caseItem.id)}
                          onChange={() => handleCaseSelect(caseItem.id)}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {caseItem.case_number}
                        </div>
                      </td>
                      <td className={`${GlobalStyles.tableCell} min-w-[120px]`}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleChatOpen(caseItem)}
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} flex items-center gap-1 relative`}
                            title="Open Chat"
                          >
                            <MessageSquare className="h-3 w-3" />
                            Chat
                            {caseItem.unread_message_count &&
                              caseItem.unread_message_count > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {caseItem.unread_message_count}
                                </span>
                              )}
                          </button>
                          {caseItem.unread_message_count &&
                          caseItem.unread_message_count > 0 ? (
                            <span className="md:hidden flex items-center gap-1">
                              <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                {caseItem.unread_message_count}
                              </span>
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.user_name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {caseItem.user_email}
                          </p>
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {formatCrypto(caseItem.amount, caseItem.currency)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          ~$
                          {formatCurrency(
                            calculateUsdValue(
                              caseItem.amount,
                              caseItem.currency
                            )
                          )}
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <span
                          className={`text-xs text-gray-500 dark:text-gray-400`}
                        >
                          {caseItem.network}
                        </span>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div className="flex items-center">
                          {getStatusIcon(caseItem.status)}
                          <span
                            className={`${
                              GlobalStyles.badge
                            } ml-2 ${getStatusColor(caseItem.status)}`}
                          >
                            {formatStatus(caseItem.status)}
                          </span>
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(caseItem.created_at).toLocaleDateString()}
                        </span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(caseItem.created_at).toLocaleTimeString(
                            [],
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </div>
                      </td>
                      <td className={`${GlobalStyles.tableCell} min-w-[180px]`}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openDetailsModal(caseItem)}
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} flex items-center gap-1`}
                            title="View Details"
                          >
                            <Eye className="h-3 w-3" />
                            Details
                          </button>
                          <div className="relative">
                            <select
                              value={caseItem.status}
                              onChange={(e) =>
                                handleStatusUpdate(caseItem.id, e.target.value)
                              }
                              disabled={updatingCaseId === caseItem.id}
                              className={`${
                                GlobalStyles.input
                              } text-sm py-1.5 pr-8 pl-3 min-w-[140px] ${
                                updatingCaseId === caseItem.id
                                  ? "opacity-70 cursor-wait"
                                  : ""
                              }`}
                            >
                              <option value="submitted">Submitted</option>
                              <option value="under_review">Under Review</option>
                              <option value="assessment_complete">
                                Assessment Complete
                              </option>
                              <option value="recovery_plan_sent">
                                Recovery Plan Sent
                              </option>
                              <option value="recovery_approved">
                                Recovery Approved
                              </option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="failed">Failed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                            {updatingCaseId === caseItem.id && (
                              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center">
                      <div className="text-center">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          {loading ? "Loading..." : "No recovery cases found"}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                          {recoveryCases.length === 0
                            ? "No recovery cases yet. They will appear here when users submit requests."
                            : "Try adjusting your filters"}
                        </p>
                        {recoveryCases.length === 0 && (
                          <button
                            onClick={handleRefresh}
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {recoveryCases.length > 0 &&
            pagination.total > pagination.per_page && (
              <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-700 gap-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing{" "}
                  <span className="font-medium">
                    {Math.min(
                      (pagination.current_page - 1) * pagination.per_page + 1,
                      pagination.total
                    )}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(
                      pagination.current_page * pagination.per_page,
                      pagination.total
                    )}
                  </span>{" "}
                  of <span className="font-medium">{pagination.total}</span>{" "}
                  cases
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      handlePageChange(pagination.current_page - 1)
                    }
                    disabled={pagination.current_page === 1}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                    title="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {Array.from(
                    { length: Math.min(5, pagination.last_page) },
                    (_, i) => {
                      let pageNum: number;
                      if (pagination.last_page <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.current_page <= 3) {
                        pageNum = i + 1;
                      } else if (
                        pagination.current_page >=
                        pagination.last_page - 2
                      ) {
                        pageNum = pagination.last_page - 4 + i;
                      } else {
                        pageNum = pagination.current_page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            pagination.current_page === pageNum
                              ? "bg-primary text-white"
                              : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() =>
                      handlePageChange(pagination.current_page + 1)
                    }
                    disabled={pagination.current_page === pagination.last_page}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                    title="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Details Modal */}
      <RecoveryDetailsModal
        caseItem={selectedCase}
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedCase(null);
        }}
        onStatusUpdate={handleStatusUpdate}
        onUpdateNotes={handleUpdateNotes}
        cryptoPrices={cryptoPrices}
      />

      {/* Statistics Modal */}
      <StatisticsModal
        statistics={statistics}
        isOpen={showStatistics}
        onClose={() => setShowStatistics(false)}
        cryptoPrices={cryptoPrices}
      />

      {/* Chat Modal */}
      {showChatModal.isOpen && (
        <RecoveryChat
          recoveryId={showChatModal.caseId}
          caseNumber={showChatModal.caseNumber}
          isAdmin={true} // This is correct for admin
          onClose={() => {
            setShowChatModal({ isOpen: false, caseId: 0, caseNumber: "" });
            fetchRecoveryCases();
            dispatch(getUnreadChatCount() as any);
          }}
          isOpen={showChatModal.isOpen}
        />
      )}
    </div>
  );
};

export default AdminRecovery;
